import React, { Component, Suspense } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import intl from 'react-intl-universal';
import { Provider, inject, observer } from 'mobx-react';
import { captureException } from '@sentry/react';

import { NotificationHost } from 'components/common/Notification/NotificationHost';
import { rememberRedirectTarget } from 'utils/redirectMemory';
import classNames from 'classnames';


import { Router } from 'AppRouter';
import { Sidebar } from 'components/layout/Sidebar/Sidebar/Sidebar';
import { ErrorBoundary } from 'components/common/ErrorBoundary/ErrorBoundary';
import { AppHeader } from '../layout/AppHeader/AppHeader';
import { LoginPage } from 'user/view/LoginPage/LoginPage';
import { LogOutPage } from 'user/view/LogOutPage/LogOutPage';
import { FeideCodeGetter } from 'user/view/FeideCodeGetter/FeideCodeGetter';

import { NewAssignmentStore } from 'assignment/view/NewAssignment/NewAssignmentStore';
import { AssignmentListStore } from 'assignment/view/AssignmentsList/AssignmentListStore';
import { CurrentQuestionaryStore } from 'assignment/view/CurrentAssignmentPage/CurrentQuestionaryStore';
import { AssignmentEvaluationStore } from 'assignment/EvaluationDraft/AssignmentEvaluationStore';
import { TeachingPathEvaluationStore } from 'teachingPath/evaluationDraft/TeachingPathEvaluationStore';
import { UIStore } from 'locales/UIStore';
import { LoginStore } from 'user/view/LoginStore';
import { EditTeachingPathStore } from 'teachingPath/view/EditTeachingPath/EditTeachingPathStore';
import { TeachingPathsListStore } from 'teachingPath/view/TeachingPathsList/TeachingPathsListStore';
import { SearchStore } from 'search/SearchStore';
import { StudentsListStore } from 'student/StudentsListStore';
import { UserType } from 'user/User';
import { QuestionaryTeachingPathStore } from 'teachingPath/questionaryTeachingPath/questionaryTeachingPathStore';
import { AppStores } from 'hooks/useStores';
import { RedirectPage } from 'user/view/RedirectPage';
import closeImg from 'assets/images/close.svg';

import { DEFAULT_LOCALE } from 'utils/constants';
import { isLocale, Locales } from 'utils/enums';

// react-datepicker's stylesheet is imported here rather than @imported at the top
// of App.scss: routed through the Sass chain it reaches resolve-url-loader with no
// source map, which warns on every build. Kept immediately above App.scss so the
// cascade order is unchanged.
import 'react-datepicker/dist/react-datepicker.css';
import './App.scss';
import { storedUserRole } from 'utils/storedUserRole';
import { messageOf } from 'utils/apiError';
import { lazyPage } from 'utils/lazyPage';
import { dayjs } from 'utils/dayjs';
import { PageLoading } from 'components/common/PageLoading/PageLoading';

/**
 * Each language's messages in a chunk of their own, about 10 KB gzipped each: all three used to
 * be in the first download, for a user who sees one (#227).
 */
const loadMessages = async (locale: Locales | ''): Promise<object> => {
  switch (locale) {
    case Locales.NB:
      return (await import('locales/nb.json')).default;
    case Locales.NN:
      return (await import('locales/nn.json')).default;
    default:
      // loadLocales has already narrowed the locale to the three this app has messages for.
      return (await import('locales/en.json')).default;
  }
};

// Each page is its own chunk, loaded the first time it is shown (#226).
const AssignStudentToAssignment = lazyPage(async () => (await import('assignment/view/AssignStudentToAssignment/AssignStudentToAssignment')).AssignStudentToAssignment);
const AssignStudentToTeachingPath = lazyPage(async () => (await import('teachingPath/view/AssignStudentToTeachingPath/AssignStudentToTeachingPath')).AssignStudentToTeachingPath);
const Submitted = lazyPage(async () => (await import('components/pages/Submited/Submitted')).Submitted);
const NewAssignment = lazyPage(async () => (await import('assignment/view/NewAssignment/NewAssignment')).NewAssignment);
const CurrentAssignmentPage = lazyPage(async () => (await import('assignment/view/CurrentAssignmentPage/CurrentAssignmentPage')).CurrentAssignmentPage);
const CurrentAssignmentPagePreview = lazyPage(async () => (await import('assignment/view/CurrentAssignmentPage/CurrentAssignmentPagePreview')).CurrentAssignmentPagePreview);
const CreateNewAssignmentFromArticle = lazyPage(async () => (await import('assignment/view/CreateNewAssignmentFromArticle/CreateNewAssignmentFromArticle')).CreateNewAssignmentFromArticle);
const PassageTeachingPath = lazyPage(async () => (await import('teachingPath/view/PassageTeachingPath/PassageTeachingPath')).PassageTeachingPath);
const PreviewTeachingPath = lazyPage(async () => (await import('teachingPath/view/PreviewTeachingPath/PreviewTeachingPath')).PreviewTeachingPath);
const EditTeachingPath = lazyPage(async () => (await import('teachingPath/view/EditTeachingPath/EditTeachingPath')).EditTeachingPath);
const ConfirmationPage = lazyPage(async () => (await import('components/pages/DistributionPage/ConfirmationPage/ConfirmationPage')).ConfirmationPage);
// import { IEMessage } from 'components/pages/IEMessage/IEMessage';

// No props: every use is a route `element` written with none, so the `props: any` these took
// and spread was always an empty object.
const CurrentAssignmentPageView = () => <CurrentAssignmentPage isTeacher />;
const CurrentAssignmentPagePreviewView = () => <CurrentAssignmentPagePreview isTeacher />;

/**
 * `useLocation` rather than a `location` prop, because there is no longer a prop to read.
 *
 * Under react-router 5 this was `component={ViewTeachingPath}` and the router injected
 * `location`. v6 renders `element={<ViewTeachingPath />}` — an element, with whatever props
 * are written on it, which here is none — so `props.location.pathname` threw and the route
 * rendered nothing but an error boundary.
 */
const ViewTeachingPath = () => {
  const { pathname } = useLocation();
  if (pathname.includes('tg=true')) {
    return (<EditTeachingPath readOnly tgOpen={true} />);
  }
  return (<EditTeachingPath readOnly />);
};
const loadDataMaintenance = 30000;
const SECOND = 2;

interface Props {
  uiStore?: UIStore;
  loginStore?: LoginStore;
}

/**
 * `/*` on the two editor paths is not decoration. Under react-router 5 a `<Route>` without
 * `exact` matched a prefix, so `/assignments/edit/5/publish` matched `/assignments/edit` and
 * the sidebar was hidden for the whole editor flow. v6 matches exactly, so without the splat
 * the sidebar reappears over the publish and distribute pages.
 */
export const routesWithoutSidebar = [
  '/assignment/:id',
  '/teaching-paths/:id',
  '/teaching-paths/edit/*',
  '/assignments/edit/*'
];

/**
 * Exported for the routing tests. These two arrays are matched in one `<Routes>`, in this
 * order, so today the first array wins any overlap — and they do overlap:
 * `/teaching-paths/:id` below matches every static `/teaching-paths/*` path above it. Under
 * react-router 6 the most specific match wins instead of the first, which should give the
 * same answer and has to be proved rather than assumed.
 */
export const routesWithSidebar = [
  '/teaching-paths/my',
  '/teaching-paths/all',
  '/teaching-paths/myschool',
  '/teaching-paths/answers/:entityId',
  '/teaching-paths/inreview'
];

/**
 * The full-screen overlay routes, one group per role, exported so they can be rendered
 * without mounting the whole application.
 *
 * `/*` on the editor paths restores what react-router 5 did without being asked. A v5
 * `<Route>` with no `exact` matched a prefix, so `/assignments/edit/5/publish` matched
 * `/assignments/edit/:id` and `NewAssignment` rendered — it switches between the editor,
 * the publish page and the distribute page by reading `location.pathname` itself rather
 * than by nesting routes. v6 matches exactly, so those URLs stopped matching anything and
 * fell through to the overlay's `null`, which is the blank page behind
 * "save goes to /publish and nothing happens".
 *
 * `/teaching-paths/view/:id/*` covers `/teaching-paths/view/:id/tg=true`, which reads like
 * a typo for `?tg=true` and is left alone here: it worked on v5 and this is a migration fix,
 * not a rewrite.
 */
export const contentManagerRoutes = (
  <>
    <Route
      path="/assignments/edit/:id/*"
      element={<NewAssignment />}
    />

    <Route
      path="/teaching-paths/edit/:id/*"
      element={<EditTeachingPath />}
    />

    <Route
      path="/teaching-paths/view/:id/*"
      element={<ViewTeachingPath />}
    />

    <Route
      path="/assignments/view/:id"
      element={<CurrentAssignmentPagePreviewView />}
    />

    <Route
      path="/assignments/preview/:id"
      element={<CurrentAssignmentPagePreviewView />}
    />

    <Route
      path="/teaching-path/preview/:id"
      element={<PreviewTeachingPath />}
    />

    <Route path="/login" element={<LoginPage />} />
    <Route path="/logout" element={<LogOutPage />} />
    <Route path="/dataporten" element={<FeideCodeGetter />} />
  </>
);

export const teacherRoutes = (
  <>
    <Route
      path="/assignments/edit/:id/*"
      element={<NewAssignment />}
    />

    <Route
      path="/assignments/edit"
      element={<CreateNewAssignmentFromArticle />}
    />
    <Route
      path="/assignments/view/:id"
      element={<CurrentAssignmentPageView />}
    />

    <Route
      path="/assignments/preview/:id"
      element={<CurrentAssignmentPagePreviewView />}
    />

    <Route
      path="/teaching-paths/edit/:id/*"
      element={<EditTeachingPath />}
    />

    <Route
      path="/teaching-paths/view/:id/*"
      element={<ViewTeachingPath />}
    />

    <Route
      path="/teaching-path/preview/:id"
      element={<PreviewTeachingPath />}
    />

    <Route
      path="/distributed"
      element={<ConfirmationPage />}
    />

    <Route path="/login" element={<LoginPage />} />
    <Route path="/logout" element={<LogOutPage />} />
    <Route path="/dataporten" element={<FeideCodeGetter />} />
  </>
);

export const studentRoutes = (
  <>
    <Route
      path="/assignment/:id"
      element={<CurrentAssignmentPage />}
    />

    <Route
      path="/teaching-path/:id"
      element={<PassageTeachingPath />}
    />
    <Route
      path="/submitted"
      element={<Submitted />}
    />

    <Route path="/login" element={<LoginPage />} />
    <Route path="/logout" element={<LogOutPage />} />
    <Route path="/dataporten" element={<FeideCodeGetter />} />
  </>
);

/**
 * The main pane's routes, exported for the same reason as the overlay groups: so a test can
 * render them without mounting the application. These two invitation URLs are the only
 * routes in the tree that no suite reached.
 */
export const mainPaneRoutes = (
          <Routes>
            <Route
              path="/assignments/:id/invitation"
              element={<AssignStudentToAssignment />}
            />
            <Route
              path="/teaching-path/:id/invitation"
              element={<AssignStudentToTeachingPath />}
            />
            {/*
              * Router renders a <Routes> of its own, so it has to sit behind a splat
              * route rather than being a child of this one: v6 accepts only <Route> and
              * fragment children and throws on anything else.
              *
              * In v5 a child without a `path` matched everything, which is how this
              * worked as a fallback. `path="/*"` is the equivalent, and it leaves the
              * whole pathname for the descendant <Routes> to match against — so the
              * absolute paths inside Router keep working unchanged.
              */}
            <Route path="/*" element={<Router />} />
          </Routes>
);

const emptyRouteStyle = {
  display: 'none',
};

@inject('uiStore', 'loginStore')
@observer
class LocalizedApp extends Component<Props> {
  private maintenancePoll?: number;

  public state = {
    isLocalesLoaded: false,
    isMaintenance: false,
    isMaintenanceClose: true,
    isDataText: ''
  };

  private renderEmptyRoute = (path: string) => (
    <Route key={path} path={path} element={<div style={emptyRouteStyle} />} />
  )

  private renderSidebar = (path: string) => (
    <Route key={path} path={path} element={<Sidebar />} />
  )

  private syncRedirectUrl() {
    rememberRedirectTarget(
      !!this.props.loginStore!.currentUser,
      window.location.href,
      window.location.pathname
    );
  }

  public maintenanceRender = async () => {
    let wpData: Awaited<ReturnType<LoginStore['getMaintenance_data']>>;
    try {
      wpData = await this.props.loginStore!.getMaintenance_data();
    } catch (error) {
      // A background check: the page stays as it is, and the next check tries again (#217).
      captureException(error);
      console.error('Could not read the maintenance status:', messageOf(error));
      return;
    }
    // `start_time` and `end_time` are only guaranteed when the window is set. They were
    // read straight into changeNewdate() while the response was `any`; undefined there
    // yields NaN, and every comparison against NaN is false — so a malformed window would
    // have silently meant "never in maintenance" rather than an error.
    if (wpData && wpData.maintenance_mode && wpData.start_time && wpData.end_time) {
      this.setState({ isDataText: wpData.maintenance_msg });
      const MyStartTimeValue = this.changeNewdate(wpData.start_time);
      const MyEndTimeValue = this.changeNewdate(wpData.end_time);
      const date = new Date();
      const exactlyHour = date.getTime();
      if (MyStartTimeValue <= exactlyHour &&  exactlyHour <= MyEndTimeValue) {
        if (!this.state.isMaintenance) { this.setState({ isMaintenance: true }); }
      } else {
        if (this.state.isMaintenance) { this.setState({ isMaintenance: false }); }
      }
    } else {
      if (this.state.isMaintenance) { this.setState({ isMaintenance: false }); }
    }
  }

  public changeNewdate = (valueConverseDate: string) => {
    const year = parseFloat(valueConverseDate.split('-')[0].split('/')[0]);
    const month = parseFloat(valueConverseDate.split('-')[0].split('/')[1]) - 1;
    const day = parseFloat(valueConverseDate.split('-')[0].split('/')[SECOND]);
    const hour = parseFloat(valueConverseDate.split('-')[1].split(':')[0]);
    const minute = parseFloat(valueConverseDate.split('-')[1].split(':')[1]);
    const response = new Date(year, month, day, hour, minute);
    const responseTime = response.getTime();
    return responseTime;
  }

  public componentDidMount() {
    void this.loadLocales();
    void this.maintenanceRender();
    this.syncRedirectUrl();
    this.maintenancePoll = window.setInterval(
      () => {
        void this.maintenanceRender();
      },
      loadDataMaintenance,
    );
  }

  public componentDidUpdate() {
    this.syncRedirectUrl();
  }

  // App never unmounts in production, so this leaked nothing there. It does under
  // StrictMode, which mounts twice and left the maintenance endpoint polled at double rate
  // in development.
  public componentWillUnmount() {
    window.clearInterval(this.maintenancePoll);
  }

  public loadLocales = async () => {
    const { uiStore, loginStore } = this.props;

    const currentLocale = localStorage.getItem('currentLocale');

    if (isLocale(currentLocale) && [Locales.EN, Locales.NB, Locales.NN].includes(currentLocale)) {
      uiStore!.setCurrentLocale(currentLocale);
    } else {
      uiStore!.setCurrentLocale(DEFAULT_LOCALE);
    }

    /**
     * `isLocalesLoaded` gates the entire application — `render` returns an empty fragment
     * until it is true — so this promise failing used to mean a permanently blank page.
     *
     * There was no `catch`. A rejection left the flag false, and what reached the user was a
     * white screen with nothing in the console, no error boundary and no route: no `.App`, no
     * header, no sidebar, not even the loader. It is the least diagnosable failure in the
     * codebase and it took a browser session and several rounds of console probing to
     * identify, because from the outside it is indistinguishable from a page that never
     * loaded.
     *
     * The catch reports the error and **lets the application render anyway**. An untranslated
     * screen showing raw message keys is a bad outcome; a blank one that says nothing is a
     * much worse one, and only the first can be reported by whoever hits it.
     */
    try {
      // Only the language in use: switching reloads the page, which loads the new one (#227).
      const { currentLocale: locale } = uiStore!;
      // Dates in the same language: "05. okt. 2026", not "05. Oct. 2026" (#229).
      if (locale) {
        dayjs.locale(locale);
      }
      const messages = await loadMessages(locale);
      await intl.init({
        locales: { [locale]: messages },
        currentLocale: locale,
      });

    } catch (error) {
      captureException(error);
      console.error(
        'Locale initialisation failed; rendering with untranslated keys rather than a blank '
        + 'page. The application is usable but every label will show its message key.',
        error,
      );
    } finally {
      this.setState({ isLocalesLoaded: true });
    }

    try {
      await loginStore!.getLocaleData();
    } catch (error) {
      captureException(error);
      console.error('Could not read the articles\' locale ids:', messageOf(error));
    }
  }

  public closeRenderMaintenance = () => {
    this.setState({ isMaintenance: false });
    this.setState({ isMaintenanceClose: false });
  }

  public renderMaintenance = () => (
    <div className="maintenance">
      <div className="maintenance__content" id="maintenance__content">
        {this.state.isDataText}
      </div>
      <div className="maintenance__close" onClick={this.closeRenderMaintenance}>
        <img src={closeImg} />
        <p>{intl.get('generals.close_maintenance')}</p>
      </div>
    </div>
  )

  public render() {
    const { isMaintenance, isLocalesLoaded } = this.state;
    const { loginStore } = this.props;

    if (!isLocalesLoaded) {
      return (<></>);
    }

    // Through storedUserRole, which catches a corrupted USER_DATA. JSON.parse here threw instead,
    // and this is the one render in the application with no error boundary above it.
    const currentUserRole = storedUserRole();
    const mainClasses = classNames('App__content', {
      App__content_shifted: this.props.uiStore!.sidebarShown
    });

    // if (isIE) return <IEMessage />;

    /*if (window.screen.width <= TABLET_WIDTH && window.screen.width > GENERAL_MOBILE_WIDTH) {
      window.addEventListener('orientationchange', (event) => {
        if (window.orientation === 0) {
          Notification.create({
            type: NotificationTypes.ERROR,
            title: intl.get('notifications.landscapeMode')
          });
        }
      });
    }*/

    return (
      <BrowserRouter>
        <div className={`App flexBox fw500 ${isMaintenance ? 'isMaintenance' : ''}`}>
          {this.state.isMaintenance && this.state.isMaintenanceClose && this.renderMaintenance()}
          {/*
            * One boundary per pane that can fail on its own. Before these existed, any
            * render-time throw anywhere unmounted the whole application and left a blank
            * page — which is how a single `.sort()` in a filter took three reports to find.
            *
            * The header and sidebar fall back to nothing: losing navigation is bad, but an
            * error panel where a menu belongs is worse, and the main pane will say what
            * happened. The main pane gets the visible message.
            */}
          <ErrorBoundary label="AppHeader" fallback={null}>
            <AppHeader />
          </ErrorBoundary>

          <div className="App__view" id="view">
            {!!loginStore!.currentUser || <Navigate to="/login" replace />}
            <div className="App__sidebar">
              <ErrorBoundary label="Sidebar" fallback={null}>
                <Routes>
                  {routesWithSidebar.map(this.renderSidebar)}
                  {routesWithoutSidebar.map(this.renderEmptyRoute)}
                  {/* `/*`, not `/`: v6 matches exactly by default, so a bare `/` would only
                      match the root URL instead of acting as the fallback it has always been. */}
                  <Route
                    path="/*"
                    element={
                      <aside className="App__sidebar">
                        {loginStore!.currentUser && <Sidebar />}
                      </aside>
                    }
                  />
                </Routes>
              </ErrorBoundary>
            </div>

            <main className={mainClasses}>
              <ErrorBoundary label="main">
                <Suspense fallback={<PageLoading />}>
                  {mainPaneRoutes}
                </Suspense>
              </ErrorBoundary>
            </main>
          </div>

          {/*
            * The role routes were the one part of this tree with no boundary over them, and
            * they hold every full-screen page: both editors, publishing, distribution,
            * evaluation. A render throw in any of them reached the root, and React 19's answer
            * to that is to unmount the entire application — the page shows for a moment and
            * then goes white, with nothing in the console, because Sentry's handler reports
            * without logging.
            *
            * That is the failure that has been reported as "something is crashing the app".
            * With a boundary here the rest of the shell survives and says which pane died.
            */}
          <ErrorBoundary label="routes">
          <Suspense fallback={<PageLoading />}>
          <Routes>

            {currentUserRole === UserType.Teacher && teacherRoutes}
            {currentUserRole === UserType.Student && studentRoutes}
            {currentUserRole === UserType.ContentManager && contentManagerRoutes}

            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<LogOutPage />} />
            <Route path="/dataporten/auth" element={<RedirectPage />} />
            <Route path="/dataporten" element={<FeideCodeGetter />} />

            {/*
              * This <Routes> is an overlay matcher, not a router: it renders the full-screen
              * pages that sit above the main pane, and on most URLs it is *meant* to render
              * nothing. v5's <Switch> did that silently; v6 logs "No routes matched location"
              * for every such URL, which is a warning about a design rather than a fault.
              *
              * The explicit no-op says so, and keeps the console clean enough that a real
              * unmatched location is still worth reading.
              */}
            <Route path="*" element={null} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </div>
      </BrowserRouter>
    );
  }
}

export class App extends Component {

  /**
   * The thirteen stores, constructed **once per mount** rather than once per render.
   *
   * They used to be `new`-ed in the argument list of `<Provider>` inside `render()`. Nothing
   * re-renders `App` today, so the only visible effect was StrictMode building two sets in
   * development and using the second — but every store in this application holds its state in
   * memory, so any future re-render of `App` would have replaced all of it silently. A field is
   * the smallest thing that cannot do that.
   *
   * Constructing them here rather than at module scope keeps two properties the old code had:
   * they are built after `src/index.tsx` has filled the injector (stores resolve services in
   * property initializers, so order matters), and a second `<App />` — which only tests do —
   * gets its own stores rather than sharing one global set.
   *
   * The `AppStores` annotation is the drift guard: a store added to the application and not
   * listed here, or listed here and not in `AppStores`, is a compile error rather than an
   * `undefined` that surfaces as a blank page somewhere.
   */
  private readonly stores: AppStores = {
    newAssignmentStore: new NewAssignmentStore(),
    assignmentListStore: new AssignmentListStore(),
    currentQuestionaryStore: new CurrentQuestionaryStore(),
    uiStore: new UIStore(),
    loginStore: new LoginStore(),
    editTeachingPathStore: new EditTeachingPathStore(),
    questionaryTeachingPathStore: new QuestionaryTeachingPathStore(),
    teachingPathsListStore: new TeachingPathsListStore(),
    assignmentEvaluationStore: new AssignmentEvaluationStore(),
    teachingPathEvaluationStore: new TeachingPathEvaluationStore(),
    studentsListStore: new StudentsListStore(),
    searchStore: new SearchStore(),
  };

  public render() {
    return (
      <Provider {...this.stores}>
        <LocalizedApp />
        <NotificationHost />
      </Provider>
    );
  }
}
